import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Container,
  Paper
} from '@mui/material';
import {
  ArrowBack,
  Warning,
  DeleteForever
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

const DeleteAccountScreen = () => {
  const navigate = useNavigate();
  const { currentUser, deleteAccount } = useAuth();
  const [password, setPassword] = useState('');
  const [confirmationText, setConfirmationText] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const handleDeleteAccount = async () => {
    if (!password.trim()) {
      setError('Please enter your password');
      return;
    }

    if (confirmationText !== 'DELETE') {
      setError('Please type DELETE to confirm account deletion');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await deleteAccount(password);
      // After successful deletion, the user will be automatically signed out
      // and redirected to the home page due to auth state change
    } catch (error) {
      console.error('Delete account error:', error);
      setLoading(false);
      
      if (error.code === 'auth/wrong-password') {
        setError('Invalid password. Please try again.');
      } else if (error.code === 'auth/too-many-requests') {
        setError('Too many failed attempts. Please try again later.');
      } else if (error.code === 'auth/user-not-found') {
        setError('User not found. Please try logging in again.');
      } else {
        setError('Failed to delete account. Please try again.');
      }
      setDeleteDialogOpen(false);
    }
  };

  const handleConfirmDelete = () => {
    setDeleteDialogOpen(true);
  };

  const isFormValid = password.trim() && confirmationText === 'DELETE';

  if (!currentUser) {
    return (
      <Container maxWidth="sm" sx={{ mt: 4 }}>
        <Alert severity="error">
          You must be logged in to delete your account.
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm" sx={{ mt: 2, mb: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <IconButton 
          onClick={() => navigate('/account')} 
          sx={{ mr: 1 }}
          aria-label="go back"
        >
          <ArrowBack />
        </IconButton>
        <Typography variant="h5" component="h1">
          Delete Account
        </Typography>
      </Box>

      {/* Warning Card */}
      <Paper 
        elevation={2} 
        sx={{ 
          p: 3, 
          mb: 3, 
          backgroundColor: 'error.light', 
          color: 'error.contrastText',
          border: '1px solid',
          borderColor: 'error.main'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Warning sx={{ mr: 1, fontSize: 28 }} />
          <Typography variant="h6" component="h2">
            Warning: This action is permanent
          </Typography>
        </Box>
        <Typography variant="body1" sx={{ mb: 2 }}>
          Deleting your account will permanently remove:
        </Typography>
        <Box component="ul" sx={{ pl: 2, mb: 0 }}>
          <li>Your profile information and settings</li>
          <li>Your betting history and preferences</li>
          <li>Your subscription and payment information</li>
          <li>All associated data and cannot be recovered</li>
        </Box>
      </Paper>

      {/* Delete Form */}
      <Card elevation={2}>
        <CardContent>
          <Typography variant="h6" component="h2" sx={{ mb: 3 }}>
            Confirm Account Deletion
          </Typography>
          
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Account: {currentUser.email}
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          <TextField
            fullWidth
            type="password"
            label="Enter your password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            sx={{ mb: 3 }}
            disabled={loading}
            required
          />

          <TextField
            fullWidth
            label='Type "DELETE" to confirm'
            value={confirmationText}
            onChange={(e) => setConfirmationText(e.target.value)}
            sx={{ mb: 3 }}
            disabled={loading}
            required
            helperText="Type DELETE in capital letters to confirm account deletion"
          />

          <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
            <Button
              variant="outlined"
              onClick={() => navigate('/account')}
              disabled={loading}
              fullWidth
              sx={{ order: { xs: 2, sm: 1 } }}
            >
              Cancel
            </Button>
            
            <Button
              variant="contained"
              color="error"
              onClick={handleConfirmDelete}
              disabled={loading || !isFormValid}
              startIcon={<DeleteForever />}
              fullWidth
              sx={{ order: { xs: 1, sm: 2 } }}
            >
              {loading ? 'Deleting Account...' : 'Delete Account'}
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Final Confirmation Dialog */}
      <Dialog 
        open={deleteDialogOpen} 
        onClose={() => !loading && setDeleteDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center' }}>
          <Warning color="error" sx={{ mr: 1 }} />
          Final Confirmation
        </DialogTitle>
        <DialogContent>
          <Typography>
            Are you absolutely sure you want to delete your account? This action cannot be undone.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Your account ({currentUser.email}) and all associated data will be permanently deleted.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button 
            onClick={() => setDeleteDialogOpen(false)} 
            disabled={loading}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleDeleteAccount} 
            color="error" 
            variant="contained"
            disabled={loading}
            startIcon={loading ? null : <DeleteForever />}
          >
            {loading ? 'Deleting...' : 'Yes, Delete My Account'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default DeleteAccountScreen;